/**
 * FeedPostCard.js — EcoScienceInnova
 * Updated with reactions, more options menu, and inline edit mode.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    FaThumbsUp, FaComment, FaShare, FaEllipsisH, FaLink, FaFileAlt,
    FaGlobe, FaUsers, FaLock, FaMapMarkerAlt, FaVideo, FaLaptop, FaCalendarAlt, FaClock
} from 'react-icons/fa';
import { FaHandsClapping } from 'react-icons/fa6';
import FeedAvatar from './FeedAvatar';
import CommentModal from './CommentModal';
import ShareModal from './ShareModal';
import ConfirmModal from '../shared/ConfirmModal';
import postService from '../../services/postService';
import './feed.shared.css';

const DEFAULT_CATEGORY_STYLES = {
    articulo: { color: '#3b82f6', label: '📄 Artículo Científico' },
    evento: { color: '#FF6600', label: '📅 Evento' },
    recurso: { color: '#22c55e', label: '🗂 Recurso' },
    discusion: { color: '#a855f7', label: '💬 Discusión' },
    post: { color: '#8a8d97', label: '📢 Publicación' },
};

const FeedPostCard = ({
    post,
    user,
    token,
    variant = 'researcher',
    categoryStyles = DEFAULT_CATEGORY_STYLES,
    onShowFeedback,
    onDelete,
    onUpdate,
    onHide,
}) => {
    const { t } = useTranslation();
    const [reaction, setReaction] = useState(post.currentUserLike || null);
    const [showReactionList, setShowReactionList] = useState(false);
    const [likeCount, setLikeCount] = useState(post.likesCount || 0);
    const [commentsCount, setCommentsCount] = useState(post.commentsCount || 0);

    const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);
    const [showOptions, setShowOptions] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editContent, setEditContent] = useState(post.content || '');
    const [editTitle, setEditTitle] = useState(post.title || '');
    const [editMetadata, setEditMetadata] = useState(post.metadata || {});
    const [showCountryList, setShowCountryList] = useState(false);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);

    const COUNTRIES = [
        { code: 'CO', name: 'Colombia', flag: '🇨🇴' },
        { code: 'ES', name: 'España', flag: '🇪🇸' },
        { code: 'MX', name: 'México', flag: '🇲🇽' },
        { code: 'AR', name: 'Argentina', flag: '🇦🇷' },
        { code: 'CL', name: 'Chile', flag: '🇨🇱' },
        { code: 'PE', name: 'Perú', flag: '🇵🇪' },
        { code: 'US', name: 'EE.UU.', flag: '🇺🇸' },
        { code: 'BR', name: 'Brasil', flag: '🇧🇷' },
        { code: 'EC', name: 'Ecuador', flag: '🇪🇨' },
        { code: 'PA', name: 'Panamá', flag: '🇵🇦' },
        { code: 'CR', name: 'Costa Rica', flag: '🇨🇷' },
    ];

    // Sync state with props when post changes
    React.useEffect(() => {
        setReaction(post.currentUserLike || null);
        setLikeCount(post.likesCount || 0);
        setCommentsCount(post.commentsCount || 0);
        setEditContent(post.content || '');
        setEditTitle(post.title || '');
        setEditMetadata(post.metadata || {});
    }, [post]);

    const handleReaction = async (type) => {
        try {
            const result = await postService.toggleLike(post.id, type, token);

            if (result.action === 'removed') {
                setReaction(null);
                setLikeCount(prev => prev - 1);
            } else if (result.action === 'added') {
                setReaction(type);
                setLikeCount(prev => prev + 1);
            } else if (result.action === 'updated') {
                setReaction(type);
            }
        } catch (err) {
            console.error('Like toggle failed:', err);
            onShowFeedback?.('Reacción', 'No pudimos registrar tu reacción. Revisa tu conexión.', 'error');
        }
        setShowReactionList(false);
    };

    const handleAction = async (type, targetId) => {
        try {
            await postService.handleFeedAction(type, targetId, token);
            onHide?.(post.id, type);
            onShowFeedback?.('Éxito', 'Acción registrada con éxito.', 'success');
        } catch (err) {
            onShowFeedback?.('Error', 'No se pudo registrar la acción.', 'error');
        }
        setShowOptions(false);
    };

    const handleDelete = async () => {
        try {
            await postService.deletePost(post.id, token);
            onDelete?.(post.id);
            onShowFeedback?.('Eliminado', 'Publicación eliminada.', 'success');
        } catch (err) {
            onShowFeedback?.('Error', 'No se pudo eliminar la publicación.', 'error');
        }
        setIsConfirmOpen(false);
        setShowOptions(false);
    };

    const handleUpdate = async () => {
        try {
            const updated = await postService.updatePost(post.id, {
                title: editTitle,
                content: editContent,
                metadata: editMetadata
            }, token);
            onUpdate?.(updated);
            setIsEditing(false);
            onShowFeedback?.('Éxito', 'Publicación actualizada.', 'success');
        } catch (err) {
            onShowFeedback?.('Error', 'No se pudo actualizar la publicación.', 'error');
        }
    };

    const category = post.type || 'post';
    const authorName = post.author?.profile
        ? `${post.author.profile.firstName} ${post.author.profile.lastName}`
        : (post.author?.name || post.author?.email || 'Usuario');

    const authorPhoto = post.author?.profile?.photoUrl || null;
    const timeAgo = new Date(post.createdAt).toLocaleDateString();

    const cat = variant === 'researcher'
        ? (categoryStyles[category] || categoryStyles.post)
        : null;

    const metadata = post.metadata || {};
    const privacy = metadata.privacy || 'public';

    const getPrivacyIcon = () => {
        switch (privacy) {
            case 'me': return <FaLock title="Solo yo" />;
            case 'groups': return <FaUsers title="Grupos" />;
            default: return <FaGlobe title="Público" />;
        }
    };

    const isDiscussion = category === 'discusion';

    return (
        <div className={`feed-post ${isDiscussion ? 'discussion-mode' : ''}`}>
            {isDiscussion && (
                <div className="discussion-forum-badge">
                    <FaComment /> <span>Foro de Debate Abierto</span>
                </div>
            )}
            <div className="feed-post-header">
                <div style={{ display: 'flex', flex: 1, gap: '12px' }}>
                    <FeedAvatar name={authorName} size={44} src={authorPhoto} />
                    <div className="feed-post-author-info">
                        <span className="feed-post-author">{authorName}</span>
                        <span className="feed-post-meta">
                            {timeAgo}
                            {' · '}
                            <span className="privacy-badge-icon">{getPrivacyIcon()}</span>
                            {variant === 'researcher' && cat && (
                                <>
                                    {' · '}
                                    <span style={{ color: cat.color, fontWeight: 600 }}>{cat.label}</span>
                                </>
                            )}
                            {metadata.feeling && (
                                <>
                                    {' · '}
                                    <span>se siente {metadata.feeling.emoji} {metadata.feeling.label}</span>
                                </>
                            )}
                        </span>
                    </div>
                </div>

                <div className="feed-post-options-container">
                    <button
                        className="feed-post-more"
                        title="Más opciones"
                        onClick={() => setShowOptions(!showOptions)}
                    >
                        <FaEllipsisH />
                    </button>

                    {showOptions && (
                        <div className="feed-options-dropdown">
                            {post.author?.id === user?.id ? (
                                <>
                                    <button onClick={() => { setIsEditing(true); setShowOptions(false); }}>Editar</button>
                                    <button className="delete-opt" onClick={() => { setIsConfirmOpen(true); setShowOptions(false); }}>Eliminar</button>
                                </>
                            ) : (
                                <>
                                    <button onClick={() => handleAction('HIDE', post.id)}>Ocultar esta publicación</button>
                                    <button onClick={() => handleAction('HIDE_30_DAYS', post.id)}>Ocultar por 30 días</button>
                                    <button onClick={() => handleAction('MUTE_SENDER', post.author?.id)}>No recibir de este remitente</button>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {post.title && <p className={isDiscussion ? "discussion-topic-title" : "feed-post-title"}>{post.title}</p>}

            {category === 'evento' && (
                <div className="feed-event-details">
                    <div className="event-info-pill">
                        <span className="event-label">CUÁNDO Y CÓMO:</span>
                        <span className="event-value">
                            {metadata.date} {metadata.time ? `· ${metadata.time}` : ''}
                            <span className="event-modality-inline">
                                ({metadata.modality === 'virtual' ? 'VIRTUAL' : 'PRESENCIAL'})
                            </span>
                        </span>
                    </div>

                    {metadata.modality !== 'virtual' ? (
                        (metadata.country || metadata.city || metadata.location) && (
                            <div className="event-info-pill location">
                                <span className="event-label">UBICACIÓN:</span>
                                <span className="event-value">
                                    {metadata.country === 'Colombia' && '🇨🇴 '}
                                    {metadata.country === 'España' && '🇪🇸 '}
                                    {metadata.country === 'México' && '🇲🇽 '}
                                    {metadata.country === 'Argentina' && '🇦🇷 '}
                                    {metadata.country === 'Chile' && '🇨🇱 '}
                                    {metadata.country === 'Perú' && '🇵🇪 '}
                                    {metadata.country === 'EE.UU.' && '🇺🇸 '}
                                    {metadata.country === 'Brasil' && '🇧🇷 '}
                                    {metadata.country === 'Ecuador' && '🇪🇨 '}
                                    {metadata.country === 'Panamá' && '🇵🇦 '}
                                    {metadata.country === 'Costa Rica' && '🇨🇷 '}
                                    {metadata.country}
                                    {metadata.city ? ` · ${metadata.city}` : ''}
                                    {metadata.location ? ` · ${metadata.location}` : ''}
                                </span>
                            </div>
                        )
                    ) : (
                        metadata.location && (
                            <div className="event-info-pill location">
                                <span className="event-label">PLATAFORMA:</span>
                                <span className="event-value">{metadata.location}</span>
                            </div>
                        )
                    )}
                </div>
            )}

            {isEditing ? (
                <div className="feed-edit-area premium-edit">
                    <div className="edit-header">
                        <span>Editar Publicación</span>
                    </div>

                    {/* Edit Title */}
                    {(category === 'evento' || category === 'articulo') && (
                        <div className="composer-field title-field">
                            <input
                                type="text"
                                value={editTitle}
                                onChange={(e) => setEditTitle(e.target.value)}
                                className="feed-edit-title-input"
                                placeholder={category === 'evento' ? "Título del Evento" : "Título del Artículo"}
                            />
                        </div>
                    )}

                    {/* Edit Metadata for Event */}
                    {category === 'evento' && (
                        <div className="composer-extra-fields">
                            <div className="composer-extra-fields row">
                                <div className="composer-field">
                                    <FaCalendarAlt className="icon" />
                                    <input
                                        type="date"
                                        value={editMetadata.date || ''}
                                        onChange={(e) => setEditMetadata({ ...editMetadata, date: e.target.value })}
                                    />
                                </div>
                                <div className="composer-field">
                                    <FaClock className="icon" />
                                    <input
                                        type="time"
                                        value={editMetadata.time || ''}
                                        onChange={(e) => setEditMetadata({ ...editMetadata, time: e.target.value })}
                                    />
                                </div>
                                <div className="composer-field modality-selector-container">
                                    <div className="modality-pills mini">
                                        <button
                                            type="button"
                                            className={`modality-pill ${editMetadata.modality === 'virtual' ? 'active' : ''}`}
                                            onClick={() => setEditMetadata({ ...editMetadata, modality: 'virtual' })}
                                        >
                                            <FaVideo /> Virtual
                                        </button>
                                        <button
                                            type="button"
                                            className={`modality-pill ${editMetadata.modality !== 'virtual' ? 'active' : ''}`}
                                            onClick={() => setEditMetadata({ ...editMetadata, modality: 'onsite' })}
                                        >
                                            <FaMapMarkerAlt /> Presencial
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="composer-extra-fields row" style={{ marginTop: '10px' }}>
                                {editMetadata.modality !== 'virtual' && (
                                    <>
                                        <div className="composer-field country-selector-field">
                                            <button
                                                type="button"
                                                className="country-picker-trigger"
                                                onClick={() => setShowCountryList(!showCountryList)}
                                            >
                                                {editMetadata.country ? (
                                                    <>
                                                        <span className="flag">{COUNTRIES.find(c => c.name === editMetadata.country)?.flag}</span>
                                                        <span className="name">{editMetadata.country}</span>
                                                    </>
                                                ) : (
                                                    <span className="placeholder">País</span>
                                                )}
                                            </button>
                                            {showCountryList && (
                                                <div className="feeling-dropdown country-dropdown">
                                                    {COUNTRIES.map(c => (
                                                        <button
                                                            key={c.code}
                                                            className="feeling-option"
                                                            onClick={() => {
                                                                setEditMetadata({ ...editMetadata, country: c.name });
                                                                setShowCountryList(false);
                                                            }}
                                                        >
                                                            <span className="emoji">{c.flag}</span>
                                                            <span className="label">{c.name}</span>
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        <div className="composer-field">
                                            <FaGlobe className="icon" />
                                            <input
                                                type="text"
                                                placeholder="Ciudad"
                                                value={editMetadata.city || ''}
                                                onChange={(e) => setEditMetadata({ ...editMetadata, city: e.target.value })}
                                            />
                                        </div>
                                    </>
                                )}
                            </div>
                            <div className="composer-field" style={{ marginTop: '10px' }}>
                                {editMetadata.modality === 'virtual' ? <FaLaptop className="icon" /> : <FaMapMarkerAlt className="icon" />}
                                <input
                                    type="text"
                                    placeholder={editMetadata.modality === 'virtual' ? "Plataforma (Zoom, Meet...)" : "Lugar / Dirección"}
                                    value={editMetadata.location || ''}
                                    onChange={(e) => setEditMetadata({ ...editMetadata, location: e.target.value })}
                                />
                            </div>
                        </div>
                    )}

                    <textarea
                        value={editContent}
                        onChange={(e) => setEditContent(e.target.value)}
                        className="feed-edit-input"
                        placeholder="Descripción..."
                        rows={4}
                    />

                    <div className="feed-edit-actions">
                        <button className="cancel-btn" onClick={() => setIsEditing(false)}>Cancelar</button>
                        <button className="save-btn" onClick={handleUpdate}>Guardar Cambios</button>
                    </div>
                </div>
            ) : (
                <p className="feed-post-content">{post.content}</p>
            )}

            {metadata.mediaUrl && (
                <div className="feed-post-media">
                    {metadata.mediaType?.startsWith('image/') ? (
                        <img
                            src={`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000'}${metadata.mediaUrl}`}
                            alt="Post media"
                            className="feed-post-img"
                        />
                    ) : metadata.mediaType?.startsWith('video/') ? (
                        <video
                            src={`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000'}${metadata.mediaUrl}`}
                            controls
                            className="feed-post-video"
                        />
                    ) : (
                        <div className="feed-post-file-box">
                            <FaFileAlt className="icon" />
                            <div className="file-info">
                                <span className="file-name">{metadata.originalName || 'Archivo adjunto'}</span>
                                <a
                                    href={`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000'}${metadata.mediaUrl}`}
                                    download
                                    className="file-download-link"
                                >
                                    Descargar
                                </a>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {category === 'articulo' && metadata.doi && (
                <a
                    className="feed-doi-badge"
                    href={metadata.doi.startsWith('http') ? metadata.doi : `https://doi.org/${metadata.doi}`}
                    target="_blank"
                    rel="noreferrer noopener"
                >
                    <FaLink /> {metadata.doi.includes('doi.org') ? '' : 'DOI: '}{metadata.doi}
                </a>
            )}

            <div className="feed-post-stats">
                <div className="stats-left">
                    {likeCount > 0 && (
                        <span className="stat-item">
                            {reaction === 'congrats' ? <FaHandsClapping style={{ color: '#FFD700', fontSize: '14px' }} /> : <FaThumbsUp style={{ color: '#FF6600', fontSize: '14px' }} />}
                            <span style={{ marginLeft: '4px' }}>{likeCount}</span>
                        </span>
                    )}
                </div>
                <div className="stats-right">
                    <span>{commentsCount} {t('community.feed.comments', 'comentarios')}</span>
                </div>
            </div>

            <div className="feed-post-actions">
                <div
                    className="reaction-wrapper"
                    onMouseEnter={() => setShowReactionList(true)}
                    onMouseLeave={() => setShowReactionList(false)}
                >
                    <button
                        className={`feed-action-btn ${reaction ? 'liked' : ''}`}
                        onClick={() => handleReaction('like')}
                    >
                        {reaction === 'congrats' ? <FaHandsClapping /> : <FaThumbsUp />}
                        <span>{reaction === 'congrats' ? 'Felicitar' : 'Me gusta'}</span>
                    </button>

                    {showReactionList && (
                        <div className="reactions-popover">
                            <button className="reaction-btn" onClick={() => handleReaction('like')} title="Me gusta"><FaThumbsUp style={{ color: '#FF6600' }} /></button>
                            <button className="reaction-btn" onClick={() => handleReaction('congrats')} title="Felicitar"><FaHandsClapping style={{ color: '#FFD700' }} /></button>
                        </div>
                    )}
                </div>

                <button className="feed-action-btn" onClick={() => setIsCommentModalOpen(true)}>
                    <FaComment />
                    <span>{isDiscussion ? 'Participar en el Debate' : t('community.feed.comment', 'Comentar')}</span>
                </button>

                <button className="feed-action-btn" onClick={() => setIsShareModalOpen(true)}>
                    <FaShare />
                    <span>{t('community.feed.share', 'Compartir')}</span>
                </button>
            </div>

            <CommentModal
                isOpen={isCommentModalOpen}
                onClose={() => setIsCommentModalOpen(false)}
                post={post}
                user={user}
                token={token}
                onShowFeedback={onShowFeedback}
                onCommentAdded={() => setCommentsCount(prev => prev + 1)}
            />
            <ShareModal
                isOpen={isShareModalOpen}
                onClose={() => setIsShareModalOpen(false)}
                post={post}
                onShowFeedback={onShowFeedback}
            />
            <ConfirmModal
                isOpen={isConfirmOpen}
                title="Eliminar Publicación"
                message="¿Estás seguro de que quieres eliminar esta publicación? Esta acción no se puede deshacer."
                onConfirm={handleDelete}
                onCancel={() => setIsConfirmOpen(false)}
            />
        </div>
    );
};

export default FeedPostCard;
